import { z } from 'zod';
import {
  custoUnitarioSchema,
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  precoUnitarioSchema,
  quantidadeSchema,
  respostaListaSchema,
  sentidoMovimentacaoEstoqueSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Movimento de estoque (`.spec/010`, "Lançamento de Estoque" e "Movimentação de
 * estoque"; `.spec/016`, seção 9).
 *
 * Dois recursos, e a diferença entre eles é a razão do módulo existir:
 *
 * - **Lançamento de estoque** é o documento que o usuário digita. Tem inclusão,
 *   leitura e exclusão.
 * - **Movimentação de estoque** é o livro-razão. Ninguém insere linha nele à
 *   mão: quem escreve é o service, junto com o documento que a originou, na
 *   mesma transação — é isso que garante que o saldo materializado em
 *   `insumos_deposito` reflita o histórico.
 *
 * Não existe `criarMovimentacaoEstoqueSchema` de propósito. Uma rota de escrita
 * no livro-razão permitiria uma movimentação sem documento e sem efeito no
 * saldo, e o job `recalcula-saldos` passaria a acusar divergência todo dia sem
 * que ninguém soubesse de onde ela veio.
 */

/* -------------------------------------------------------------------------- */
/* Lançamento de estoque                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Quantidade movimentada: **sempre positiva**.
 *
 * O sentido do material é o tipo de movimentação escolhido, nunca o sinal do
 * número. Aceitar negativo criaria duas formas de dizer a mesma coisa — uma
 * "entrada de −10" e uma "saída de 10" —, e a soma que forma o saldo teria de
 * decidir qual das duas obedecer.
 */
const quantidadeDoLancamentoSchema = quantidadeSchema.refine(
  (v) => Number(v) > 0,
  'A quantidade movimentada deve ser maior que zero.',
);

/**
 * Preço unitário da entrada: **nunca negativo**.
 *
 * `precoUnitarioSchema` aceita sinal — a expressão base é `/^-?…/` —, e faltava a
 * barreira. Material entrando por valor negativo forma custo médio negativo no
 * depósito, e toda saída seguinte sai por ele: o erro não aparece no lançamento,
 * aparece na margem da obra meses depois. É o mesmo risco que a decisão 34
 * descreve para a data de 2099.
 *
 * O sintoma que denunciou a falta era pior do que a falta: sem código próprio, o
 * `valor_invalido` que o domínio devolvia caía no `estoque_insuficiente` do
 * service, e a API respondia "saldo insuficiente para a saída" a uma **entrada**.
 * Ver `paraErroDeDominio`, no service.
 *
 * **Zero passa**, e é `>= 0` e não `> 0` de propósito — a mesma escolha de
 * `precoNaoNegativoSchema` na cotação, no pedido e na nota. Material que entra
 * sem custo existe: bonificação, doação, devolução de sobra. O único caso em que
 * zero é recusado é o acerto que estreia o material num depósito sem custo médio,
 * e essa recusa é do service, que é quem sabe se a linha já tem média.
 */
const precoDaEntradaSchema = precoUnitarioSchema.refine(
  (v) => Number(v) >= 0,
  'O valor unitário não pode ser negativo.',
);

/**
 * Payload de gravação.
 *
 * **Não tem `tipo` nem `valorTotal`, e o `valorUnitario` é opcional.** As três
 * ausências são a mesma decisão: o servidor é quem sabe.
 *
 * - `tipo` é o sentido do tipo de movimentação escolhido. Aceitá-lo aqui abriria
 *   a única forma de os dois discordarem — uma perda gravada como entrada.
 * - `valorTotal` é `quantidade × valorUnitario`. Recebê-lo permitiria um total
 *   que não confere com os fatores, e o valor do estoque passaria a andar por
 *   um número que ninguém consegue reproduzir.
 * - `valorUnitario` só existe na **entrada**, que é onde o custo se forma. Na
 *   saída o valor é o custo médio vigente no depósito, e pedi-lo ao usuário
 *   deixaria o documento contradizer a baixa que o saldo sofreu.
 */
export const criarLancamentoEstoqueSchema = z.object({
  /** Empresa do grupo — `pessoas` com `is_empresa`. É a dona do depósito. */
  empresaId: uuidSchema,
  /** Dia do movimento. Data de negócio, sem hora: o lançamento é do dia 10. */
  dataLancamento: dataDoFatoSchema,
  /** Motivo do movimento. É ele que diz o sentido e se o custo é atualizado. */
  tipoMovimentacaoEstoqueId: uuidSchema,
  /** Insumo movimentado. Sempre material: serviço não movimenta estoque. */
  insumoId: uuidSchema,
  /** Depósito onde o material entra ou de onde sai. */
  depositoId: uuidSchema,
  quantidade: quantidadeDoLancamentoSchema,
  /**
   * Preço unitário da entrada — até **6 casas**.
   *
   * Ausente na saída — e ausente, não zero: `0` é um custo declarado, e o
   * servidor não teria como distinguir "não informei" de "custa nada". Quando
   * vem numa saída, é ignorado; a decisão de ignorar em vez de recusar está no
   * service, junto com o motivo.
   *
   * **Seis casas, e antes eram duas.** A coluna
   * `lancamentos_estoque.valor_unitario` sempre teve seis — é `custoUnitario` —,
   * e o payload as cortava: o litro de diesel a `6,123456` entrava no estoque
   * como `6,12`, e o custo médio nascia errado de origem. Quantas casas o campo
   * mostra é do cadastro do insumo (`casas_decimais_preco`); o que se confere
   * aqui é o teto da coluna. Ver `precoUnitarioSchema`.
   *
   * **Não aceita negativo.** Ver `precoDaEntradaSchema`, acima.
   */
  valorUnitario: precoDaEntradaSchema.optional(),
  /**
   * Motivo do movimento, em texto livre. **Opcional** (`.spec/015`, decisão 52).
   *
   * Era obrigatória, com mínimo de três caracteres, pelo argumento de que
   * movimento manual sem motivo escrito não se audita. O argumento não
   * sobreviveu ao uso: acerto de inventário e devolução de sobra são o uso
   * normal da tela, e a exigência produzia "ajuste" digitado em todo lançamento
   * — texto repetido por obrigação não se lê depois. O que explica o movimento
   * são o tipo de movimentação e o par insumo × depósito, todos obrigatórios.
   *
   * É a mesma travessia que o lançamento de consumo fez em 11/09/2026 (decisão
   * 44). A assimetria que aquela decisão registrou — "o do lançamento de estoque
   * continua" — deixou de existir.
   */
  observacao: z.string().trim().max(500, 'A observação tem no máximo 500 caracteres.').prefault(''),
});

export type CriarLancamentoEstoque = z.infer<typeof criarLancamentoEstoqueSchema>;

/**
 * Lançamento como a API o devolve.
 *
 * Os nomes de insumo, depósito, empresa e tipo vêm **desnormalizados**: a
 * listagem mostra "Cimento CP-II · Almoxarifado Central" e não dois uuid, e
 * resolver isso no cliente significaria duas consultas por linha — 50 idas ao
 * servidor para desenhar uma página.
 */
export const lancamentoEstoqueSchema = z.object({
  id: uuidSchema,
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  dataLancamento: dataSchema,
  /** Cópia do sentido do tipo de movimentação, resolvida pelo servidor. */
  tipo: sentidoMovimentacaoEstoqueSchema,
  tipoMovimentacaoEstoqueId: uuidSchema,
  tipoMovimentacaoEstoqueNome: z.string().nullable(),
  /** Se este movimento formou custo. Cópia congelada do cadastro. */
  atualizaCusto: z.boolean(),
  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  depositoId: uuidSchema,
  depositoNome: z.string().nullable(),
  /** Strings decimais, nunca number: ponto flutuante perde centavos em silêncio. */
  quantidade: quantidadeSchema,
  /**
   * Seis casas: na saída esta é a **taxa** — o custo médio do momento —, e o
   * dinheiro é `valorTotal`. Ver `custoUnitarioSchema`.
   */
  valorUnitario: custoUnitarioSchema,
  valorTotal: dinheiroSchema,
  observacao: z.string(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type LancamentoEstoque = z.infer<typeof lancamentoEstoqueSchema>;

export const listaLancamentosEstoqueSchema = respostaListaSchema(lancamentoEstoqueSchema);
export type ListaLancamentosEstoque = z.infer<typeof listaLancamentosEstoqueSchema>;

/**
 * Filtros da listagem.
 *
 * Sem `ativo`: lançamento é documento de movimento, não cadastro — ele existe
 * ou não existe. O recorte natural é **depósito e período**, que é como se
 * procura um lançamento ("o que entrou no canteiro em julho"), e é exatamente o
 * índice que a tabela tem.
 */
export const listarLancamentosEstoqueQuerySchema = paginacaoSchema.extend({
  empresaId: uuidSchema.optional(),
  depositoId: uuidSchema.optional(),
  insumoId: uuidSchema.optional(),
  tipo: sentidoMovimentacaoEstoqueSchema.optional(),
  tipoMovimentacaoEstoqueId: uuidSchema.optional(),
  /** Início do período, inclusivo. */
  de: dataSchema.optional(),
  /** Fim do período, inclusivo. */
  ate: dataSchema.optional(),
});

export type ListarLancamentosEstoqueQuery = z.infer<typeof listarLancamentosEstoqueQuerySchema>;

export const COLUNAS_ORDENAVEIS_LANCAMENTO_ESTOQUE = [
  'dataLancamento',
  'quantidade',
  'valorTotal',
  'createdAt',
] as const;

/**
 * O sentido **pode** ter valor unitário digitado?
 *
 * Vive no contrato, e não no formulário nem na API, porque os dois precisam da
 * **mesma** regra: a tela decide se o campo é editável, e o service decide se lê
 * o que chegou ou calcula do custo médio. Duas cópias divergiriam na primeira
 * correção, e a divergência apareceria como valor de estoque que ninguém
 * reproduz.
 *
 * Responde só pelo **sentido**, e por isso não basta sozinha: uma entrada de
 * tipo que não forma custo também não digita valor. Quem responde a pergunta
 * inteira é `valorUnitarioEditavel`, logo abaixo — é ela que a tela e o service
 * chamam. Esta continua sendo o primeiro degrau daquela, e o que separa a saída,
 * que nunca digita, de tudo o mais.
 */
export function exigeValorUnitario(
  tipo: z.infer<typeof sentidoMovimentacaoEstoqueSchema>,
): boolean {
  return tipo === 'ENTRADA';
}

/**
 * O movimento manual, do ponto de vista de **quem decide o valor unitário**.
 *
 * Três dados, e nenhum deles responde sozinho: o sentido diz se há custo a
 * formar, a marca do tipo diz se este movimento o forma, e o custo médio da
 * linha diz se já existe custo formado para herdar.
 */
export interface MovimentoDoLancamentoEstoque {
  /** Sentido do tipo de movimentação escolhido. */
  sentido: z.infer<typeof sentidoMovimentacaoEstoqueSchema>;
  /** O tipo **forma custo** — `tipos_movimentacao_estoque.atualiza_custo`. */
  atualizaCusto: boolean;
  /**
   * Custo médio vigente na linha insumo × depósito, decimal em string.
   *
   * `null` enquanto o depósito não foi escolhido — na tela isso acontece o tempo
   * todo, e cai no mesmo caso do depósito que ainda não formou custo.
   */
  custoMedio: string | null;
}

/** A linha já formou custo? Vazio e zero são a mesma resposta: ainda não. */
export function temCustoMedio(custoMedio: string | null): boolean {
  return custoMedio !== null && custoMedio.trim() !== '' && Number(custoMedio) > 0;
}

/**
 * O valor unitário se **digita**, ou sai do custo médio do depósito?
 *
 * Três respostas, e a do meio é a razão de a função existir:
 *
 * - **Saída**: nunca se digita. O valor é o custo médio vigente, e a saída o
 *   consome em vez de formá-lo.
 * - **Entrada de um tipo que não forma custo** — acerto de inventário, devolução
 *   de sobra de obra — num depósito que **já tem custo médio**: também não se
 *   digita. O material que entra é o mesmo que já está lá, e vale o que a linha
 *   diz que vale. Enquanto o campo era digitável, o valor informado entrava na
 *   média assim mesmo: com 1 un a R$ 40,00 no depósito, um acerto de 1 un
 *   digitado a R$ 100,00 levava a média para R$ 70,00. Um tipo marcado como
 *   "não atualiza custo" reprecificava o material inteiro — a marca dizia uma
 *   coisa na tela e o saldo fazia outra —, e todo consumo de obra dali em diante
 *   saía pelo custo errado.
 * - **Entrada de um tipo que não forma custo, num depósito sem custo médio**:
 *   digita-se, e o service exige um valor maior que zero. Herdar zero poria o
 *   material no estoque valendo nada; aqui o acerto é a primeira entrada daquele
 *   material naquele local, e alguém tem de dizer quanto ele vale.
 *
 * A entrada que **forma** custo é o caso simples: digita-se sempre, porque é
 * dela que o custo nasce.
 *
 * **Não vale para a transferência entre depósitos**, cujos dois tipos padrão
 * também têm `atualiza_custo` desligado: lá a entrada do destino recebe o valor
 * que saiu da **origem** (`.spec/015`, decisão 30), e ninguém digita nada. Esta
 * regra é do lançamento manual, que é onde há um campo para preencher.
 */
export function valorUnitarioEditavel(movimento: MovimentoDoLancamentoEstoque): boolean {
  if (!exigeValorUnitario(movimento.sentido)) return false;
  if (movimento.atualizaCusto) return true;

  return !temCustoMedio(movimento.custoMedio);
}

/* -------------------------------------------------------------------------- */
/* Movimentação de estoque — o kardex                                         */
/* -------------------------------------------------------------------------- */

/**
 * Origem da movimentação, já resolvida para a tela.
 *
 * As cinco colunas de origem da tabela são mutuamente exclusivas por `CHECK`.
 * Devolver o código em vez de cinco campos nulos evita que o cliente
 * reimplemente essa exclusividade.
 *
 * `TRANSFERENCIA` é a única que aparece **duas vezes por documento de origem**:
 * o item da transferência gera uma saída no depósito de origem e uma entrada no
 * de destino, e as duas apontam para a mesma linha. É por isso que o índice
 * único dela inclui o `tipo` — ver `movimentacoes_estoque`.
 */
export const ORIGENS_MOVIMENTACAO_ESTOQUE = [
  'LANCAMENTO',
  'NOTA',
  'CONSUMO',
  'TRANSFERENCIA',
  // A quinta, de 26/09/2026 (decisão 82): a entrada do material que voltou da
  // obra. Não é `LANCAMENTO` porque ela tira o valor do custo da obra, e o
  // lançamento manual de entrada não tira.
  'DEVOLUCAO_CONSUMO',
] as const;
export const origemMovimentacaoEstoqueSchema = z.enum(ORIGENS_MOVIMENTACAO_ESTOQUE);
export type OrigemMovimentacaoEstoque = z.infer<typeof origemMovimentacaoEstoqueSchema>;

export const ROTULO_ORIGEM_MOVIMENTACAO_ESTOQUE: Readonly<
  Record<OrigemMovimentacaoEstoque, string>
> = {
  LANCAMENTO: 'Lançamento manual',
  NOTA: 'Nota de compra',
  CONSUMO: 'Consumo de obra',
  TRANSFERENCIA: 'Transferência entre depósitos',
  DEVOLUCAO_CONSUMO: 'Devolução de consumo',
};

export const movimentacaoEstoqueSchema = z.object({
  id: uuidSchema,
  empresaId: uuidSchema,
  dataLancamento: dataSchema,
  tipo: sentidoMovimentacaoEstoqueSchema,
  tipoMovimentacaoEstoqueNome: z.string().nullable(),
  atualizaCusto: z.boolean(),
  insumoDepositoId: uuidSchema,
  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  depositoId: uuidSchema,
  depositoNome: z.string().nullable(),
  quantidade: quantidadeSchema,
  /** Seis casas, como no lançamento — ver `lancamentoEstoqueSchema`. */
  valorUnitario: custoUnitarioSchema,
  valorTotal: dinheiroSchema,
  obraId: uuidSchema.nullable(),
  origem: origemMovimentacaoEstoqueSchema,
  /** Id do documento de origem — o lançamento, hoje; a nota e o consumo, depois. */
  origemId: uuidSchema,
  observacao: z.string(),
  createdAt: dataHoraSchema,
});

export type MovimentacaoEstoque = z.infer<typeof movimentacaoEstoqueSchema>;
